import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Github } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { projects, Project } from '../data/projects';

gsap.registerPlugin(ScrollTrigger);

const ProjectCard: React.FC<{ project: Project; index: number; featured?: boolean }> = ({ project, featured }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative group rounded-xl overflow-hidden border border-white/10 bg-dark-700 transition-all duration-500 hover:border-accent/40 ${
        featured ? 'md:col-span-2 md:row-span-2' : 'col-span-1 row-span-1'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={project.images[0]}
          alt={project.name}
          className="w-full h-full object-cover opacity-50 group-hover:opacity-20 group-hover:scale-110 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/70 to-dark-900/30" />
      </div>

      {/* Content */}
      <div className={`relative z-10 p-6 h-full flex flex-col justify-end ${featured ? 'min-h-[350px]' : 'min-h-[220px]'}`}>
        <div className={`transform transition-all duration-500 ${isHovered ? 'translate-y-0' : 'translate-y-4'}`}>
          <h3 className="font-bold text-lg md:text-xl mb-2 text-white group-hover:text-accent transition-colors duration-300">
            {project.name}
          </h3>

          <p className={`text-gray-400 text-sm leading-relaxed mb-4 transition-all duration-500 ${
            isHovered ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0'
          } overflow-hidden`}>
            {project.details}
          </p>

          {/* Tech stack pills */}
          <div className={`flex flex-wrap gap-2 mb-4 transition-all duration-500 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}>
            {project.technologies.map((tech) => (
              <span
                key={tech}
                className="text-xs font-mono px-2 py-0.5 rounded border border-accent/30 text-accent/80 bg-accent/5"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Links */}
          <div className={`flex gap-3 transition-all duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-accent transition-colors"
            >
              <Github size={14} />
              Code
            </a>
            {project.urlLink && (
              <a
                href={project.urlLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-neon-cyan transition-colors"
              >
                <ExternalLink size={14} />
                Live
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Neon border glow on hover */}
      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none border border-accent/50 shadow-neon" />
    </div>
  );
};

const Projects: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headingRef.current, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          end: 'top 60%',
          scrub: 1,
        },
        opacity: 0,
        x: -50,
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Sort: featured first
  const sortedProjects = [...projects].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return 0;
  });

  return (
    <section id="projects" ref={sectionRef} className="min-h-screen py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <h2
          ref={headingRef}
          className="font-mono text-sm text-accent uppercase tracking-[0.3em] mb-12"
        >
          {'// PROJECTS'}
        </h2>

        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          style={{ gridAutoRows: 'minmax(220px, auto)' }}
        >
          {sortedProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={index}
              featured={project.featured}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
